import React, { FC, memo, ReactNode, useCallback, useMemo } from "react";
import ColumnSlider from "../ColumnSlider/ColumnSlider";
import useWindowSizeDebounced from "@hooks/useWindowSizeDebounced";
import cn from "classnames";
import "./Ribbon.styles.scss";

export interface RibbonItem {
    id: string | number;
    title: string;
    description?: string;
    url?: string;
    imageUrl?: string;
    content?: React.ReactNode;

    [key: string]: any;
}

export interface iProps {
    items: RibbonItem[];
    maxSlidesPerView?: number;
    minTileWidth?: number;
    className?: string;
    renderItem?: (item: RibbonItem, index: number, isActive: boolean) => React.ReactNode;
    children?: React.ReactNode;
    activeTileId?: string | number;
    onTileClick?: (item: RibbonItem, index: number) => void;
    setActiveTileIndex?: (index: number | null) => void;
    autoSlideIntervalMs?: number;
    itemVariations?: Array<'text-always' | 'landscape'>;
    isAutoSlideDisabled?: boolean;
    hideBuiltInNav?: boolean;
    onNavReady?: (handlers: { next: () => void; prev: () => void }) => void;
    pinnedFirstTile?: boolean;
    renderBadge?: (item: RibbonItem, index: number) => React.ReactNode;
}

function getSlidesPerView(viewportWidth: number, approxTileWidth: number): number {
    const availableWidth = viewportWidth - 100; // Account for padding/margins
    return Math.floor(availableWidth / approxTileWidth);
}

const Ribbon: FC<iProps> = ({
    items = [],
    maxSlidesPerView = Infinity,
    minTileWidth = 100,
    className,
    renderItem,
    children,
    activeTileId,
    onTileClick,
    setActiveTileIndex,
    autoSlideIntervalMs,
    isAutoSlideDisabled,
    itemVariations = [],
    hideBuiltInNav,
    onNavReady,
    pinnedFirstTile,
    renderBadge,
}) => {
    const [wWidth] = useWindowSizeDebounced(200);

    // Calculate slides per view based on window width and tile width
    const calculatedSlidesPerView = useMemo(() => {
        const calculated = getSlidesPerView(wWidth, minTileWidth);
        return Math.max(1, Math.min(calculated, maxSlidesPerView));
    }, [wWidth, minTileWidth, maxSlidesPerView]);

    const handleTileClick = useCallback((item: RibbonItem, index: number) => {
        if (onTileClick) {
            onTileClick(item, index);
        }
    }, [onTileClick]);

    const defaultRenderItem = useCallback((item: RibbonItem, index: number, isActive: boolean) => {
        return (
            <a
                href={item.url ?? ""}
                target="_blank"
                rel="noreferrer"
                key={item.id}
                className={cn(
                    "Ribbon__item",
                    {
                        "is-active": isActive,
                    }
                )}
                style={{
                    backgroundImage: item.imageUrl ? `url(${item.imageUrl})` : undefined,
                }}
                onClick={() => handleTileClick(item, index)}
            >
                {renderBadge && (
                    <div className="Ribbon__item-badge">
                        {renderBadge(item, index)}
                    </div>
                )}
                <div className="Ribbon__item-image-sizer">
                    <img
                        src={item.imageUrl}
                        alt={item.title}
                    />
                </div>
                <div className="Ribbon__item-content">
                    {item.content || (
                        <div className="Ribbon__item-fallback">
                            <div className="Ribbon__item-fallback-title">{item.title}</div>
                            <div className="Ribbon__item-fallback-description">{item.description}</div>
                        </div>
                    )}
                </div>
            </a>
        );
    }, [handleTileClick, itemVariations, renderBadge]);

    const sliderItems = useMemo(() => pinnedFirstTile ? items.slice(1) : items, [items, pinnedFirstTile]);
    const pinnedItem = useMemo(() => pinnedFirstTile ? items[0] : null, [items, pinnedFirstTile]);

    const renderedItems: ReactNode[] = useMemo(() => {
        const renderedItems: ReactNode[] = sliderItems.map((item, index) => {
            const originalIndex = pinnedFirstTile ? index + 1 : index;
            const isActive = activeTileId !== undefined && item.id === activeTileId;
            return renderItem ? renderItem(item, originalIndex, isActive) : defaultRenderItem(item, originalIndex, isActive);
        });

        if (sliderItems.length > calculatedSlidesPerView) renderedItems.push(children);

        return renderedItems;
    }, [sliderItems, activeTileId, renderItem, defaultRenderItem, calculatedSlidesPerView, children, pinnedFirstTile]);

    const handleSliderActiveTileIndex = useCallback((index: number | null) => {
        if (pinnedFirstTile && index !== null) {
            setActiveTileIndex?.(index + 1);
        } else {
            setActiveTileIndex?.(index);
        }
    }, [pinnedFirstTile, setActiveTileIndex]);

    const pinnedRendered = useMemo(() => {
        if (!pinnedItem) return null;
        return renderItem
            ? renderItem(pinnedItem, 0, true)
            : defaultRenderItem(pinnedItem, 0, true);
    }, [pinnedItem, renderItem, defaultRenderItem]);

    return (
        <div
            className={cn(
                "Ribbon",
                className,
                pinnedFirstTile && "is-pinned-layout",
                ...itemVariations.map(i => `is-item-${i}`),
            )}
        >
            <div className="Ribbon__content">
                {pinnedFirstTile && pinnedRendered && (
                    <div className="Ribbon__pinned-tile">
                        {pinnedRendered}
                    </div>
                )}
                <div className={cn("Ribbon__slider-area", pinnedFirstTile && "Ribbon__slider-area--with-pinned")}>
                    <ColumnSlider
                        slidesPerView={calculatedSlidesPerView}
                        isNewNav
                        setActiveTileIndex={pinnedFirstTile ? handleSliderActiveTileIndex : setActiveTileIndex}
                        autoSlideIntervalMs={autoSlideIntervalMs}
                        isAutoSlideDisabled={isAutoSlideDisabled}
                        hideNav={hideBuiltInNav}
                        onNavReady={onNavReady}
                    >
                        {renderedItems}
                    </ColumnSlider>
                </div>
            </div>
        </div>
    );
};

export default memo(Ribbon);
